/**
 * The native TanStack title mutation.
 *
 * Writes go to a dedicated Rails action rather than `POST /_graphql`: the client sends the issue
 * *number* in the path, so the endpoint can only ever write the issue the URL already authorized.
 */
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {UpdateIssueTitleMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueTitleMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {ERRORS} from '../constants/errors'
import {escapeOptimisticTitleHTML} from '../utils/helpers'

type UpdateIssueTitleVariables = {nameWithOwner: string; issueNumber: number; title: string}

const titleOf = (operation: SetFieldsOperation): string => String(operation.fields.title ?? '')

async function updateIssueTitleRequest({
  nameWithOwner,
  issueNumber,
  title,
}: UpdateIssueTitleVariables): Promise<UpdateIssueTitleMutationMutation> {
  let response: Response
  try {
    response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/title`, {
      method: 'PUT',
      body: {title},
    })
  } catch {
    throw new Error(ERRORS.couldNotUpdateIssueTitle)
  }

  let result:
    | {data?: UpdateIssueTitleMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null}
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const errors = result?.errors
  if (!response.ok) {
    throw new Error(errors?.[0]?.message ?? ERRORS.couldNotUpdateIssueTitle)
  }

  if (!result?.data?.updateIssue?.issue) {
    throw new Error(errors?.[0]?.message ?? ERRORS.couldNotUpdateIssueTitle)
  }

  return result.data
}

export function useUpdateIssueTitle({
  issueId,
  nameWithOwner,
  issueNumber,
  onError,
  onCompleted,
}: {
  issueId: string
  nameWithOwner: string
  issueNumber: number
  onError?: (error: Error) => void
  onCompleted?: () => void
}) {
  const {mutate} = useEntityMutation<UpdateIssueTitleMutationMutation>({
    mutationFn: operation => updateIssueTitleRequest({nameWithOwner, issueNumber, title: titleOf(operation)}),
    getConfirmedOperation: data => {
      const issue = data.updateIssue?.issue
      if (!issue) return undefined
      return {type: 'setFields', id: issue.id, fields: {title: issue.title, titleHTML: issue.titleHTML}}
    },
    onSuccess: () => onCompleted?.(),
    onError: error => onError?.(error),
  })

  return useCallback(
    (title: string) => {
      const operation: SetFieldsOperation = {
        type: 'setFields',
        id: issueId,
        fields: {title, titleHTML: escapeOptimisticTitleHTML(title)},
      }
      mutate({operation})
    },
    [issueId, mutate],
  )
}
